import FlashMessage from '@atoms/FlashMessage/FlashMessage';
import { BG_CREME, BG_GREEN, BG_WHITE } from '@constants';
import { vacancyPageType } from '@lib/proptypes';
import PagepartBase from '@molecules/PagepartBase/PagepartBase';
import ApplicationForm from '@organisms/ApplicationForm/ApplicationForm';
import ApplicationProcess from '@organisms/ApplicationProcess/ApplicationProcess';
import DetailHero from '@organisms/DetailHero/DetailHero';
import ImageSliderSection from '@organisms/ImageSliderSection/ImageSliderSection';
import JobAlertSection from '@organisms/JobAlertSection/JobAlertSection';
import Sections from '@organisms/Sections/Sections';
import StoriesSection from '@organisms/StoriesSection/StoriesSection';
import TextSection from '@organisms/TextSection/TextSection';
import WhatToExpectSection from '@organisms/WhatToExpectSection/WhatToExpectSection';
import { isPast } from 'date-fns';
import Head from 'next/head';
import { useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

const RECRUITER_PLACEHOLDER_IMAGE = {
  url: '/img/recruiter_placeholder.png',
  placeholder: '/img/recruiter_placeholder_blur.png',
  width: 210,
  height: 210,
};

const FALLBACK_RECRUITER_BLOCK = {
  title: 'Heb je een vraag?',
  text: 'Bekijk de veelgestelde vragen of neem contact op met één van onze recruiters.',
  recruiter: [
    {
      name: 'Vitalis recruitment',
      image: RECRUITER_PLACEHOLDER_IMAGE,
      phone: [
        {
          id: 'phone-recruiter',
          isWhatsapp: false,
          phone: '0610473613',
          title: '06 - 10 47 36 13',
        },
      ],
      email: [
        {
          id: 'email-recruiter',
          email: 'werken@vitalisgroep.nl',
          title: 'werken@vitalisgroep.nl',
        },
      ],
    },
  ],
};

const JOB_ALERT_BLOCK = [
  {
    title: 'Op de hoogte blijven van nieuwe vacatures?',
    text: 'Activeer jouw vacaturemelding en ontvang de nieuwste vacatures in je mailbox.',
    firstCtaText: 'Meld me aan',
  },
];

function VacancyDetail({ page }) {
  const locationTitle =
    page.aggregationSummary?.locations?.length === 1
      ? page.aggregationSummary.locations[0].title
      : 'Meerdere locaties';
  const vacancyDetails = {
    minHours: page.aggregationSummary?.minHoursPerWeek,
    maxHours: page.aggregationSummary?.maxHoursPerWeek,
    minSalary: page.aggregationSummary?.minSalaryIndication,
    maxSalary: page.aggregationSummary?.maxSalaryIndication,
    education: page.educationLevel?.title,
    location: locationTitle,
  };
  const closingDateHasExpired = isPast(new Date(page.closingDate));

  const recruiterBlock = [
    {
      ...FALLBACK_RECRUITER_BLOCK,
      recruiter: [
        {
          ...FALLBACK_RECRUITER_BLOCK.recruiter[0],
          ...page.recruiter,
        },
      ],
    },
  ];

  const applicationProcessPath = page.applicationProcess?.items[0];
  const applicationFormRef = useRef(null);
  const { ref: inViewRef, inView } = useInView();

  /* See: https://www.npmjs.com/package/react-intersection-observer#:~:text=How%20can%20I%20assign%20multiple%20refs%20to%20a%20component%3F */
  const setRefs = useCallback(
    node => {
      // Ref's from useRef needs to have the node assigned to `current`
      applicationFormRef.current = node;
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node);
    },
    [inViewRef]
  );

  return (
    <>
      {closingDateHasExpired && (
        <Head>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
      )}
      <DetailHero
        title={page.title}
        text={page.shortDescription}
        image={page.headerImage}
        vacancyDetails={vacancyDetails}
        paddingBottom={!page?.sections?.length}
        modifier="vacancyDetail"
        ctaBlock={!closingDateHasExpired}
        applicationFormRef={applicationFormRef}
        formInView={inView}
        highlightedMessage={
          closingDateHasExpired
            ? 'Deze vacature is gesloten. Je kunt niet meer solliciteren.'
            : null
        }
      />
      <ImageSliderSection
        id="vacancy-atmospheric-images"
        items={page.fieldOfWork?.atmosphericImages}
        backgroundColor={{ color: BG_WHITE }}
        displayScrollBar
      />
      {!!page.workingPlaceDescription && (
        <TextSection
          id="vacancy-working-place-description"
          title="Dit zijn wij"
          text={page.workingPlaceDescription}
          backgroundColor={{ color: BG_WHITE }}
          alignment="left"
          isMarkdown
        />
      )}
      {!!page.jobDescription && (
        <TextSection
          id="vacancy-job-description"
          title="Dit ga jij doen"
          text={page.jobDescription}
          backgroundColor={{ color: BG_CREME }}
          alignment="left"
          isMarkdown
        />
      )}
      {!!page.yourProfile && (
        <TextSection
          id="vacancy-your-profile"
          title="Dit breng jij mee"
          text={page.yourProfile}
          backgroundColor={{ color: BG_WHITE }}
          alignment="left"
          isMarkdown
        />
      )}
      {!!page.employmentConditions && (
        <TextSection
          id="vacancy-employment-conditions"
          title="Dit bieden wij"
          text={page.employmentConditions}
          backgroundColor={{ color: BG_WHITE }}
          alignment="left"
          isMarkdown
        />
      )}
      {page.locationDetails?.uspsTitle && page.locationDetails?.usps && (
        <TextSection
          id="location-detail-usps"
          title={page.locationDetails.uspsTitle}
          text={page.locationDetails.usps}
          backgroundColor={{ color: BG_CREME }}
        />
      )}
      <StoriesSection
        id="vacancy-stories"
        title={page.fieldOfWork?.storiesTitle}
        text={page.fieldOfWork?.storiesText}
        stories={page.storyPages?.stories}
      />
      <WhatToExpectSection
        id="vacancy-what-to-expect"
        list={page.fieldOfWork?.whatToExpectItems}
        backgroundColor={{
          color: BG_GREEN,
        }}
      />
      {!!page.contactInformation && (
        <TextSection
          id="vacancy-apply-section"
          title="Solliciteer"
          text={page.contactInformation}
          backgroundColor={{ color: BG_WHITE }}
          alignment="left"
          isMarkdown
        />
      )}
      <JobAlertSection
        id="vacancy-job-alert-section"
        recruiterBlock={recruiterBlock}
        jobAlertBlock={JOB_ALERT_BLOCK}
        backgroundColor={{
          color: BG_GREEN,
        }}
      />
      {!!applicationProcessPath && (
        <ApplicationProcess
          id="vacancy-application-process"
          title={applicationProcessPath.title}
          text={applicationProcessPath.text}
          list={applicationProcessPath.applicationProcessItems}
        />
      )}
      <Sections sections={page.fieldOfWork?.sections} />

      {closingDateHasExpired ? (
        <PagepartBase backgroundColor={BG_CREME}>
          <FlashMessage
            type="info"
            message="Deze vacature is gesloten. Je kunt niet meer solliciteren."
          />
        </PagepartBase>
      ) : (
        <ApplicationForm
          vacancyCode={page.code}
          aggregationChildren={page.aggregationChildren}
          aggLocationDetails={page.aggLocationDetails}
          applicationCode={page.applicationForm?.code}
          applicationFormRef={setRefs}
          vacancyTitle={page.title}
        />
      )}
    </>
  );
}

VacancyDetail.propTypes = {
  page: vacancyPageType.isRequired,
};

export default VacancyDetail;
